import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Sparkles, LayoutDashboard, LogOut, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { NAV } from '@/constants/testIds';

const links = [
  { to: '/', label: 'Početna', testid: NAV.linkHome },
  { to: '/usluge', label: 'Usluge', testid: NAV.linkServices },
  { to: '/podrucja', label: 'Područja', testid: NAV.linkAreas },
  { to: '/saveti', label: 'Saveti', testid: NAV.linkBlog },
  { to: '/recenzije', label: 'Recenzije', testid: NAV.linkReviews },
  { to: '/rezervacija', label: 'Rezervacija', testid: NAV.linkBooking },
  { to: '/o-nama', label: 'O nama', testid: NAV.linkAbout },
  { to: '/kontakt', label: 'Kontakt', testid: NAV.linkContact },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled ? 'glass shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="container-x flex items-center justify-between py-4">
        <Link
          to="/"
          data-testid={NAV.brand}
          className="flex items-center gap-2 font-display font-extrabold text-xl tracking-tight"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl text-white"
            style={{ background: 'linear-gradient(135deg, #023E8A 0%, #5B21B6 100%)' }}
          >
            <Sparkles className="w-5 h-5" />
          </span>
          <span>
            Dubinko<span className="text-emerald-600">.</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-semibold transition ${
                  isActive
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'
                }`
              }
              end={l.to === '/'}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              {user.role === 'admin' ? (
                <Link
                  to="/admin"
                  data-testid={NAV.linkAdmin}
                  className="btn-ghost px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2"
                >
                  <ShieldCheck className="w-4 h-4" /> Admin
                </Link>
              ) : (
                <Link
                  to="/nalog"
                  data-testid={NAV.linkDashboard}
                  className="btn-ghost px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2"
                >
                  <LayoutDashboard className="w-4 h-4" /> Moj nalog
                </Link>
              )}
              <button
                onClick={() => { logout(); navigate('/'); }}
                data-testid={NAV.logoutBtn}
                className="btn-ghost px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" /> Odjava
              </button>
            </>
          ) : (
            <>
              <Link
                to="/prijava"
                data-testid={NAV.linkLogin}
                className="btn-ghost px-4 py-2 rounded-full text-sm font-semibold"
              >
                Prijava
              </Link>
              <Link
                to="/rezervacija"
                data-testid={NAV.linkBooking}
                className="btn-primary px-5 py-2 rounded-full text-sm font-semibold"
              >
                Zakaži čišćenje
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 rounded-xl border border-slate-200"
          data-testid={NAV.mobileToggle}
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden glass border-t border-white/40">
          <div className="container-x py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-base font-semibold ${
                    isActive ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
                  }`
                }
                end={l.to === '/'}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="h-px bg-slate-200 my-2" />
            {user ? (
              <>
                {user.role === 'admin' ? (
                  <Link to="/admin" className="px-4 py-3 rounded-xl font-semibold">Admin</Link>
                ) : (
                  <Link to="/nalog" className="px-4 py-3 rounded-xl font-semibold">Moj nalog</Link>
                )}
                <button onClick={() => { logout(); navigate('/'); }}
                  className="px-4 py-3 rounded-xl font-semibold text-left">
                  Odjava
                </button>
              </>
            ) : (
              <>
                <Link to="/prijava" className="px-4 py-3 rounded-xl font-semibold">Prijava</Link>
                <Link to="/rezervacija" className="px-4 py-3 rounded-xl font-semibold btn-primary text-center">
                  Zakaži čišćenje
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
